import React, {
  ImgHTMLAttributes,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Loader } from 'ncoded-component-library';
import logo from 'assets/images/logo.webp';
import noImgAvatar from 'assets/images/no-img-avatar.webp';
import LazyLoad from 'react-lazy-load';
import utils from '../../utils';
import { Size } from 'models/Size';

import './ResolveImage.styles.scss';

type ResolveImageProps = {
  className?: string;
  rootProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  asAvatar?: boolean;
  asCompanyLogo?: boolean;
  asIllustration?: boolean;
  loaderSize?: Size;
  shouldLazyLoad?: boolean;
  getImageSize?: (naturalWidth: number, naturalHeight: number) => void;
} & ImgHTMLAttributes<HTMLImageElement>;

const ResolveImage: React.FC<ResolveImageProps> = (props) => {
  const {
    className,
    rootProps,
    asAvatar,
    asCompanyLogo,
    asIllustration,
    loaderSize,
    alt,
    title,
    src = asAvatar ? noImgAvatar : logo,
    shouldLazyLoad = true,
    getImageSize,
    ...rest
  } = props;
  const [isLoaded, setIsLoaded] = useState(false);

  const classes = classNames('resolve-image', className, {
    avatar: asAvatar,
    company: asCompanyLogo,
    illustration: asIllustration,
  });

  const onError = useCallback(
    ({ currentTarget }: SyntheticEvent<HTMLImageElement>) => {
      currentTarget.onerror = null;
      (currentTarget as HTMLImageElement).src = logo;
    },
    [],
  );

  const onLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget;
      getImageSize?.(naturalWidth, naturalHeight);
      setIsLoaded(true);
    },
    [getImageSize],
  );

  const ImageElement = useMemo(
    () => (
      <img
        src={src}
        alt={alt || title}
        onError={onError}
        onLoad={onLoad}
        {...rest}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src, alt, title, onError, onLoad],
  );

  // In case pre-rendering for SEO optimization
  if (utils.isSSGBot()) {
    return (
      <div className={classes} {...rootProps}>
        <div className="LazyLoad is-visible">{ImageElement}</div>
      </div>
    );
  }

  return (
    <div className={classes} {...rootProps}>
      {!isLoaded && <Loader size={loaderSize} />}

      {shouldLazyLoad ? (
        <LazyLoad>{ImageElement}</LazyLoad>
      ) : (
        <div className="not-lazy-load">{ImageElement}</div>
      )}
    </div>
  );
};

export default React.memo(ResolveImage);
